import { useContext, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { apiGet, apiPost, apiPatch, apiDelete } from 'api'
import AuthContext from 'context/auth-context'
import UserContext from 'context/user-context'
import { useDashboard } from './use-dashboards'

// The named taxonomies a municipality rolls its accounts up under — "OCAS", "ACFR". Reporting
// stays inside one: a View reports under its own choice, everything else under the default.

const GROUPINGS_KEY = 'account-groupings'

const getAccountGroupings = async (token, municipalityId) =>
  apiGet({ url: `municipalities/${municipalityId}/account_groupings`, token })

const createAccountGrouping = async (token, municipalityId, params) =>
  apiPost({
    url: `municipalities/${municipalityId}/account_groupings`,
    token,
    objectName: 'account_grouping',
    params
  })

const updateAccountGrouping = async (token, id, params) =>
  apiPatch({ url: `account_groupings/${id}`, token, objectName: 'account_grouping', params })

const deleteAccountGrouping = async (token, id) => apiDelete({ url: `account_groupings/${id}`, token })

export const useAccountGroupings = ({ options } = {}) => {
  const token = useContext(AuthContext)
  const { municipality_id } = useContext(UserContext)

  return useQuery(
    [GROUPINGS_KEY, municipality_id],
    () => getAccountGroupings(token, municipality_id),
    { ...options }
  )
}

// The API orders defaults first, so the first row matches the server's own fallback.
export const defaultGrouping = (groupings) =>
  (groupings || []).find((grouping) => grouping.is_default) || (groupings || [])[0] || null

// Same precedence as AccountGroups::GroupingResolver: declared, then the View's choice, then
// the default. Declared wins because on a report the View's choice is inherited context.
export const useActiveAccountGrouping = (dashboardId, declaredId = null) => {
  const { data: groupings } = useAccountGroupings({})
  const { data: dashboard } = useDashboard({
    id: dashboardId,
    options: { enabled: Boolean(dashboardId) && !declaredId }
  })

  return useMemo(() => {
    const declared = declaredId &&
      (groupings || []).find((grouping) => grouping.id === declaredId)
    if (declared) return declared

    const chosen = (groupings || []).find(
      (grouping) => grouping.id === dashboard?.account_grouping_id
    )

    return chosen || defaultGrouping(groupings)
  }, [groupings, dashboard?.account_grouping_id, declaredId])
}

// Changes what every rollup column buckets by, so it invalidates what a group edit does.
const useInvalidatingMutation = (mutationFn, options) => {
  const queryClient = useQueryClient()

  return useMutation(mutationFn, {
    ...options,
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries(GROUPINGS_KEY)
      queryClient.invalidateQueries('account-groups')
      queryClient.invalidateQueries('dashboard-chart-data')
      queryClient.invalidateQueries('report-chart-data')
      queryClient.invalidateQueries('transactions')
      options?.onSuccess?.(data, variables)
    }
  })
}

export const useCreateAccountGrouping = ({ options } = {}) => {
  const token = useContext(AuthContext)
  const { municipality_id } = useContext(UserContext)

  return useInvalidatingMutation(
    (params) => createAccountGrouping(token, municipality_id, params),
    options
  )
}

export const useUpdateAccountGrouping = ({ options } = {}) => {
  const token = useContext(AuthContext)

  return useInvalidatingMutation(
    ({ id, params }) => updateAccountGrouping(token, id, params),
    options
  )
}

export const useDeleteAccountGrouping = ({ options } = {}) => {
  const token = useContext(AuthContext)

  return useInvalidatingMutation((id) => deleteAccountGrouping(token, id), options)
}
